import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSportsbookBetslip from './use-sportsbook-betslip';
import { SelectionDto } from '../api/sbapi/sbapi-api-model';
import { useLocation, type Location } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import * as Api from '../api/sportsbook-api';
import { selectIsReadyForApiCalls } from '../sportsbook-root-selectors';
import { AppDispatch } from '~/store/types';
import SportsbookSelectionTrackingTags from '~/components/sportsbook/lobby/sportsbook-selection-tracking-tags';
import { selectSportsbookUnifiedFeedEnabled } from '~/components/brand/ducks/selectors';
import normalizeUnifiedId from '../utils/normalize-unified-id';

const ADD_BET_QUERY_PARAM = 'addbet';
const ADD_TAG_QUERY_PARAM = 'addbettag';

const useConsumeQuerySelections = () => {
	const location = useLocation() as Location<{ search: string }>;
	const router = useRouter();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { addSelectionsToBetslip } = useSportsbookBetslip();
	const isApiReady: boolean = useSelector(selectIsReadyForApiCalls);
	const isUnifiedFeedEnabled = useSelector(selectSportsbookUnifiedFeedEnabled);
	const dispatch: AppDispatch = useDispatch();

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const selectionIds = queryParams
			.getAll(ADD_BET_QUERY_PARAM)
			.flatMap(id => id.split(',')) // Split by comma after to treat potentially encoded commas that was not extrated by queryParams.getAll
			.filter((value, index, self) => self.indexOf(value) === index);
		const selectionTag = queryParams.get(ADD_TAG_QUERY_PARAM);

		if (isApiReady && !isLoading && selectionIds?.length > 0) {
			setIsLoading(true);
			Api.fetchSelections(isUnifiedFeedEnabled ? selectionIds.map(normalizeUnifiedId) : selectionIds)
				.then((data: SelectionDto[]) => {
					if (selectionTag) {
						addSelectionsToBetslip(data, selectionTag);
					}
					addSelectionsToBetslip(data, SportsbookSelectionTrackingTags.URL_GENERAL);
				})
				.finally(() => {
					setIsLoading(false);

					queryParams.delete(ADD_BET_QUERY_PARAM);
					queryParams.delete(ADD_TAG_QUERY_PARAM);
					router.replace(`${location.pathname}?${queryParams.toString()}`);
				})
				.catch(() => {});
		}
	}, [
		addSelectionsToBetslip,
		router,
		isLoading,
		isApiReady,
		dispatch,
		location.search,
		location.pathname,
		isUnifiedFeedEnabled
	]);
};

export default useConsumeQuerySelections;
